import React, { useMemo } from 'react';
import { Select, Box, Text, Alert, Loader, Group, ActionIcon } from '@mantine/core';
import { IconX, IconAlertCircle } from '@tabler/icons-react';
import { useSchemas, useSchemaLoading, useSchemaError } from '../../store/schemaHooks';
import { debugLog } from '../../utils/debugLog';

/** Minimal shape for entity type option (e.g. from tier1 entitlements) */
export interface EntityTypeOption {
    name: string;
    id?: string;
}

interface EntityTypeSelectorProps {
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    allowClear?: boolean;
    excludeTypes?: string[];
    /** When set, use these types instead of Redux schema (e.g. tier1 entitlements). Stops empty dropdown when schema is disabled. */
    entityTypesOverride?: EntityTypeOption[] | null;
}

export const EntityTypeSelector: React.FC<EntityTypeSelectorProps> = ({
    value,
    onChange,
    label = "Entity Type",
    required = false,
    disabled = false,
    error = false,
    helperText,
    allowClear = true,
    excludeTypes = [],
    entityTypesOverride
}) => {
    // Normalize value: component expects string | null; older code or API may pass an array
    const normalizedValue: string | null = (() => {
        if (value == null || value === '') return null;
        if (Array.isArray(value)) {
            const first = value.find((v: any) => v != null && v !== '');
            return first != null ? String(first) : null;
        }
        return String(value);
    })();

    const schemas = useSchemas();
    const loading = useSchemaLoading();
    const schemaError = useSchemaError();

    const entityTypes = useMemo(() => {
        if (entityTypesOverride !== undefined && entityTypesOverride !== null) {
            const list = entityTypesOverride.map((t) => ({ name: t.name, id: t.id ?? t.name }));
            if (excludeTypes.length > 0) {
                return list.filter((entityType) =>
                    !excludeTypes.some((excluded) =>
                        (entityType.name ?? '').toLowerCase() === excluded.toLowerCase() ||
                        (entityType.id ?? '').toLowerCase() === excluded.toLowerCase()
                    )
                );
            }
            return list;
        }
        let filteredTypes = schemas || [];
        if (excludeTypes.length > 0) {
            filteredTypes = filteredTypes.filter((entityType: any) =>
                !excludeTypes.some(excluded =>
                    entityType.name?.toLowerCase() === excluded.toLowerCase() ||
                    entityType.id?.toLowerCase() === excluded.toLowerCase()
                )
            );
        }
        return filteredTypes;
    }, [schemas, excludeTypes.join(','), entityTypesOverride]);

    const handleClear = () => {
        onChange(null);
    };

    const selectData = useMemo(() => {
        const options = entityTypes.map((entityType: any) => {
            const typeValue = entityType.name || entityType.id;
            return { value: typeValue, label: typeValue };
        });
        // If we have a value that doesn't exist in the loaded types, add it
        if (normalizedValue) {
            const valueExists = options.some((opt: any) => opt.value.toLowerCase() === normalizedValue.toLowerCase());
            if (!valueExists) {
                const suffix = (entityTypesOverride == null && loading) ? ' (loading...)' : ' (not in list)';
                options.push({ value: normalizedValue, label: normalizedValue + suffix });
            }
        }
        return options;
    }, [entityTypes, normalizedValue, loading, entityTypesOverride]);

    return (
        <Box>
            <Select
                label={label}
                placeholder="Select an entity type..."
                data={selectData}
                value={normalizedValue}
                onChange={(val) => {
                    const selected = val || null;
                    debugLog('[EntityTypeSelector] selected value:', selected);
                    onChange(selected);
                }}
                required={required}
                disabled={(entityTypesOverride == null ? loading : false) || disabled}
                error={error}
                searchable
                clearable={allowClear}
                rightSection={
                    (entityTypesOverride == null && loading) ? <Loader size={16} /> : undefined
                }
            />

            {entityTypesOverride == null && schemaError && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} mt="xs">
                    {schemaError}
                </Alert>
            )}

            {helperText && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {helperText}
                </Text>
            )}
        </Box>
    );
};
